import React, { PropsWithChildren, ReactNode, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { useAdminAuth } from "../auth/AdminAuthProvider";
import { AdminTopHeader } from "./AdminTopHeader";
import { PageTitleBar } from "./ui";

// 관리자 주요 화면만 숨김 라우트 묶음으로 고정해 이동 경로를 단순하게 유지한다.
const navItems = [
  { href: "/", label: "홈페이지 콘텐츠 관리" },
  { href: "/history", label: "홈페이지 콘텐츠 업데이트 내역" },
  { href: "/config", label: "최근 사이트 설정 내역" },
  { href: "/storage", label: "Firebase 파일 관리" },
];

type AdminShellProps = PropsWithChildren<{
  title: string;
  description?: string;
  rightPanel?: ReactNode;
  titleActions?: ReactNode;
  pageClassName?: string;
}>;

/**
 * 관리자 공통 레이아웃 셸.
 * 좌측 네비게이션/상단 타이틀/자동 로그아웃(idle) 정책을 페이지 공통으로 적용한다.
 */
export function AdminShell({ title, description, rightPanel, titleActions, pageClassName, children }: AdminShellProps) {
  const navigate = useNavigate();
  const { user, logout } = useAdminAuth();
  const idleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user) return;

    const idleMs = 30 * 60 * 1000;
    const events: Array<keyof WindowEventMap> = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    let timedOut = false;

    // 기존 idle 타이머를 정리해 중복 타임아웃을 방지한다.
    const clearIdleTimer = () => {
      if (idleTimerRef.current != null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    // 유휴 시간 초과 시 로그아웃 후 로그인 페이지로 강제 이동한다.
    const onIdleTimeout = async () => {
      if (timedOut) return;
      timedOut = true;
      clearIdleTimer();
      try {
        await logout();
      } finally {
        navigate("/login?reason=idle", { replace: true });
      }
    };

    // 사용자 활동이 감지되면 idle 타이머를 재시작한다.
    const resetIdleTimer = () => {
      if (timedOut) return;
      clearIdleTimer();
      idleTimerRef.current = window.setTimeout(() => {
        void onIdleTimeout();
      }, idleMs);
    };

    events.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    });

    resetIdleTimer();

    return () => {
      clearIdleTimer();
      events.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer);
      });
    };
  }, [logout, navigate, user]);

  return (
    <div className="cm-shell">
      <AdminTopHeader />
      <div className={`cm-layout ${rightPanel ? "has-right-panel" : ""}`.trim()}>
        <aside className="cm-side">
          <div className="cm-side-top">
            <div className="cm-side-auth">
              <span className={`cm-pill ${user ? "ok" : "warn"}`.trim()}>{user ? "로그인됨" : "비로그인"}</span>
              <div className="cm-muted">Email: {user?.email || "(없음)"}</div>
              <div className="cm-muted">UID: {user?.uid || "(없음)"}</div>
            </div>
            <button
              className="cm-btn cm-btn-danger cm-side-logout"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              type="button"
            >
              로그아웃
            </button>
          </div>
          <nav className="cm-side-nav">
            {navItems.map((item) => (
              <NavLink className={({ isActive }) => `cm-side-link ${isActive ? "active" : ""}`.trim()} key={item.href} to={item.href}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        {/* pageClassName: 특정 화면에만 스타일 스코프를 걸기 위한 내부 확장 포인트 */}
        <main className={`cm-main ${pageClassName || ""}`.trim()}>
          <section className="cm-page-card">
            <PageTitleBar title={title} description={description} right={titleActions} />
            {children}
          </section>
        </main>
        {rightPanel ? <aside className="cm-right-panel">{rightPanel}</aside> : null}
      </div>
    </div>
  );
}
